import { Head, useForm } from "@inertiajs/react";
import { Download, FileUp, Upload } from "lucide-react";
import type { ChangeEvent, FormEventHandler } from "react";
import { useRef } from "react";

import InputError from "@/components/input-error";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import AppLayout from "@/layouts/app-layout";
import {
  exportApprovalsWorkflowsPath,
  importApprovalsWorkflowsPath,
  importNewApprovalsWorkflowsPath,
  sectorsPath,
} from "@/routes";
import type { BreadcrumbItem } from "@/types";

interface ApprovalWorkflowsImportProps {
  max_levels: number;
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Sectores", href: sectorsPath() },
  {
    title: "Importar niveles de aprobación",
    href: importNewApprovalsWorkflowsPath(),
  },
];

export default function ApprovalWorkflowsImport({ max_levels }: ApprovalWorkflowsImportProps) {
  const { data, setData, post, processing, errors, reset } = useForm({
    file: null as File | null,
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const submit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    if (!data.file) return;

    post(importApprovalsWorkflowsPath(), {
      forceFormData: true,
      onFinish: () => reset("file"),
    });
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setData("file", file);
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Importar niveles de aprobación" />
      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex flex-col space-y-2">
          <h2 className="text-2xl font-bold">Importar niveles de aprobación</h2>
          <p className="text-muted-foreground">
            Descargá el Excel de referencia, actualizá los aprobadores por obra y subilo para sincronizarlos.
          </p>
        </div>

        <Alert>
          <AlertTitle>Cómo funciona</AlertTitle>
          <AlertDescription>
            Usá la hoja &quot;Niveles&quot; del Excel para editar hasta {max_levels} niveles por obra. Los niveles vacíos no eliminan asignaciones y cada fila se procesa de forma independiente.
          </AlertDescription>
        </Alert>

        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-muted-foreground">
            Paso 1: descargá la configuración vigente.
          </div>
          <Button asChild variant="outline" className="gap-2">
            <a href={exportApprovalsWorkflowsPath()}>
              <Download className="h-4 w-4" />
              Exportar niveles (xlsx)
            </a>
          </Button>
        </div>

        <Card className="max-w-3xl">
          <CardHeader>
            <CardTitle>Paso 2: cargá el Excel actualizado</CardTitle>
            <CardDescription>
              Solo se aceptan archivos Excel (.xlsx) con la hoja &quot;Niveles&quot; y la columna obligatoria sector_id.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-6" onSubmit={submit}>
              <div className="space-y-3">
                <p className="text-sm font-medium">Seleccioná el archivo .xlsx a importar</p>
                <input
                  ref={fileInputRef}
                  id="workflow-import-file"
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileChange}
                  disabled={processing}
                  className="hidden"
                />
                <div className="flex flex-col gap-2 rounded-md border border-dashed border-muted p-4 text-sm">
                  <p className="text-muted-foreground">
                    Elegí el archivo exportado previamente, completado con los aprobadores por nivel.
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="gap-2"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={processing}
                    >
                      <FileUp className="h-4 w-4" />
                      Seleccionar archivo (.xlsx)
                    </Button>
                    {data.file ? (
                      <span className="text-xs text-muted-foreground">
                        {data.file.name}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        Ningún archivo seleccionado
                      </span>
                    )}
                  </div>
                </div>
                <InputError message={errors.file} />
              </div>

              <div className="space-y-2 text-sm text-muted-foreground">
                <p className="font-medium">Recordatorios clave:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Hasta {max_levels} niveles por sector.</li>
                  <li>Los emails se normalizan a minúsculas y deben existir en el sistema.</li>
                  <li>No se eliminan asignaciones existentes, sólo se agregan las nuevas.</li>
                  <li>Cada fila que falle se omite sin afectar al resto.</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button type="submit" disabled={processing || !data.file} className="gap-2">
                  <Upload className="h-4 w-4" /> Importar niveles
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={processing}
                  onClick={() => {
                    reset("file");
                    setData("file", null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                >
                  Limpiar selección
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
